import { argbFromHex, Hct, hexFromArgb, TonalPalette } from "@material/material-color-utilities";
import { alpha, getContrastRatio } from "@mui/material/styles";
import type { TenantThemeConfig } from "@/config/themes/types";
import { THEME_MODE } from "@/constants";

/**
 * A tenant's two brand colours, as `#rrggbb`. Everything else in a branded theme is derived:
 *
 * - **primary** drives what you act on — buttons, the selected tab and nav item, links, focus.
 * - **secondary** drives the brand surfaces — the sidebar and modal-header gradient, its border,
 *   the accent used for outlines, and the supporting chart colours.
 *
 * Only the colours that carry the brand are replaced. Status colours, neutrals and text stay
 * Olive's, so errors still read as errors and body text keeps its tested contrast.
 */
export type BrandColors = {
  primary: string;
  secondary: string;
};

/** WCAG AA for body text — anything written on, or in, a primary colour. */
const MIN_TEXT_CONTRAST = 4.5;
/** WCAG AA for non-text graphics — icons, outlines, focus rings. */
const MIN_GRAPHIC_CONTRAST = 3;

const WHITE = "#FFFFFF";

/**
 * A seed broken into hue, chroma and tone (HCT, the colour space Material Design 3 uses). Tone is
 * perceptual lightness, 0 black to 100 white, so "the same colour, lighter" is the same hue and
 * chroma at a higher tone — no hue drift, unlike lightening in HSL.
 */
type Seed = {
  hex: string;
  palette: TonalPalette;
  hue: number;
  chroma: number;
  tone: number;
};

function seedOf(hex: string): Seed {
  const hct = Hct.fromInt(argbFromHex(hex));
  return {
    hex,
    palette: TonalPalette.fromHueAndChroma(hct.hue, hct.chroma),
    hue: hct.hue,
    chroma: hct.chroma,
    tone: Math.round(hct.tone),
  };
}

function toneHex(seed: Seed, tone: number): string {
  return hexFromArgb(seed.palette.tone(clampTone(tone)));
}

/** The seed's hue at `tone`, with its chroma capped — for tints that should whisper the brand. */
function mutedToneHex(seed: Seed, tone: number, maxChroma: number): string {
  const muted = TonalPalette.fromHueAndChroma(seed.hue, Math.min(seed.chroma, maxChroma));
  return hexFromArgb(muted.tone(clampTone(tone)));
}

/** The seed's hue at `tone`, with a share of its chroma — a tint that fades as the brand's does. */
function scaledToneHex(seed: Seed, tone: number, chromaShare: number): string {
  const scaled = TonalPalette.fromHueAndChroma(seed.hue, seed.chroma * chromaShare);
  return hexFromArgb(scaled.tone(clampTone(tone)));
}

/**
 * The sidebar's stops as (tone, share of the seed's chroma), measured off Olive's own hand-tuned
 * sidebar so that Olive's green derives Olive's sidebar and every other brand gets the same shape.
 *
 * The shares matter more than the tones. A tonal palette holds chroma constant as it lightens, so
 * a light stop at the seed's full chroma comes out far more vivid than the brand it came from —
 * Olive's light stops fade to about two-thirds and one-third of the seed's colourfulness instead.
 */
const SIDEBAR_STOPS = {
  LIGHT_CORNER: { tone: 65, chromaShare: 1 },
  LIGHT_CENTER: { tone: 86, chromaShare: 0.65 },
  LIGHT_EDGE: { tone: 94, chromaShare: 0.31 },
  LIGHT_BORDER: { tone: 67, chromaShare: 0.7 },
  DARK_CORNER: { tone: 36, chromaShare: 0.8 },
  DARK_CENTER: { tone: 6, chromaShare: 0.3 },
} as const;

function sidebarStop(seed: Seed, stop: { tone: number; chromaShare: number }): string {
  return scaledToneHex(seed, stop.tone, stop.chromaShare);
}

function clampTone(tone: number): number {
  return Math.min(100, Math.max(0, Math.round(tone)));
}

/**
 * The nearest tone to `startTone`, moving in `direction`, whose colour reaches `minContrast`
 * against `against`. Falls back to the far end of the range, which always contrasts most.
 */
function toneMeetingContrast(
  seed: Seed,
  startTone: number,
  against: string,
  minContrast: number,
  direction: "darker" | "lighter",
): number {
  const step = direction === "darker" ? -1 : 1;
  for (let tone = clampTone(startTone); tone >= 0 && tone <= 100; tone += step) {
    if (getContrastRatio(toneHex(seed, tone), against) >= minContrast) {
      return tone;
    }
  }
  return direction === "darker" ? 0 : 100;
}

/** The seed itself when it already meets `minContrast`, so a brand colour is kept exact when it can be. */
function exactOrAdjusted(
  seed: Seed,
  against: string,
  minContrast: number,
  direction: "darker" | "lighter",
): { hex: string; tone: number } {
  if (getContrastRatio(seed.hex, against) >= minContrast) {
    return { hex: seed.hex, tone: seed.tone };
  }
  const tone = toneMeetingContrast(seed, seed.tone, against, minContrast, direction);
  return { hex: toneHex(seed, tone), tone };
}

function brandGradient(cornerColor: string, centerColor: string, edgeColor: string): string {
  return `linear-gradient(135deg, ${cornerColor} 0%, ${centerColor} 15%, ${edgeColor} 85%, ${cornerColor} 100%)`;
}

function deriveLight(base: TenantThemeConfig, primary: Seed, secondary: Seed): TenantThemeConfig {
  // Primary is both a fill under white text and a text colour on white surfaces; one 4.5:1 check
  // against white covers both. A colour too light for that is darkened along its own hue.
  const main = exactOrAdjusted(primary, WHITE, MIN_TEXT_CONTRAST, "darker");
  const lightestGraphicTone = toneMeetingContrast(primary, 100, WHITE, MIN_GRAPHIC_CONTRAST, "darker");
  const accent = exactOrAdjusted(secondary, WHITE, MIN_GRAPHIC_CONTRAST, "darker");
  const primaryLight = toneHex(
    primary,
    Math.max(main.tone, Math.min(main.tone + 15, lightestGraphicTone)),
  );
  const sidebarCorner = sidebarStop(secondary, SIDEBAR_STOPS.LIGHT_CORNER);

  return {
    ...base,
    colors: {
      ...base.colors,
      primary: main.hex,
      primaryDark: toneHex(primary, main.tone - 16),
      primaryLight,
      hoverPrimary: toneHex(primary, main.tone - 8),
      textOnPrimary: WHITE,
      secondary: mutedToneHex(secondary, 93, 8),
      accent: accent.hex,
      gradientBackground: brandGradient(
        sidebarCorner,
        sidebarStop(secondary, SIDEBAR_STOPS.LIGHT_CENTER),
        sidebarStop(secondary, SIDEBAR_STOPS.LIGHT_EDGE),
      ),
      sidebarBorder: sidebarStop(secondary, SIDEBAR_STOPS.LIGHT_BORDER),
      chartPalette: [
        main.hex,
        accent.hex,
        base.colors.success,
        base.colors.info,
        base.colors.warning,
        primaryLight,
        sidebarCorner,
      ],
    },
    overlay: {
      ...base.overlay,
      olive15: alpha(toneHex(primary, 40), 0.15),
    },
  };
}

function deriveDark(base: TenantThemeConfig, primary: Seed, secondary: Seed): TenantThemeConfig {
  const textOnPrimary = base.colors.textOnPrimary;
  const surface = base.colors.surface;
  // A dark-mode fill has to stay dark enough for light text, yet not vanish into the background.
  const darkestVisibleTone = 30;
  const lightestFillTone = toneMeetingContrast(
    primary,
    100,
    textOnPrimary,
    MIN_TEXT_CONTRAST,
    "darker",
  );
  const fillTone = Math.min(
    Math.max(primary.tone, Math.min(darkestVisibleTone, lightestFillTone)),
    lightestFillTone,
  );
  const keepsExactSeed =
    fillTone === primary.tone &&
    getContrastRatio(primary.hex, textOnPrimary) >= MIN_TEXT_CONTRAST;
  const mainHex = keepsExactSeed ? primary.hex : toneHex(primary, fillTone);
  const accent = exactOrAdjusted(secondary, surface, MIN_GRAPHIC_CONTRAST, "lighter");
  // Icons and highlights sit on dark surfaces, so they lift until they are visible there.
  const primaryLight = toneHex(
    primary,
    toneMeetingContrast(primary, fillTone + 8, surface, MIN_GRAPHIC_CONTRAST, "lighter"),
  );
  const secondaryTint = mutedToneHex(secondary, 91, 8);

  return {
    ...base,
    colors: {
      ...base.colors,
      primary: mainHex,
      primaryDark: toneHex(primary, fillTone - 8),
      primaryLight,
      hoverPrimary: toneHex(primary, fillTone + 6),
      textOnPrimary,
      secondary: secondaryTint,
      accent: accent.hex,
      gradientBackground: brandGradient(
        sidebarStop(secondary, SIDEBAR_STOPS.DARK_CORNER),
        sidebarStop(secondary, SIDEBAR_STOPS.DARK_CENTER),
        sidebarStop(secondary, SIDEBAR_STOPS.DARK_CENTER),
      ),
      chartPalette: [
        mainHex,
        base.colors.info,
        base.colors.success,
        base.colors.warning,
        primaryLight,
        accent.hex,
        secondaryTint,
      ],
    },
    overlay: {
      ...base.overlay,
      olive15: alpha(toneHex(primary, 50), 0.2),
    },
  };
}

/**
 * `base` with every brand-carrying colour derived from `brand`, for `base`'s own mode.
 *
 * Material Design 3's approach: each seed becomes a tonal palette (one hue and chroma, every
 * lightness), and each role takes the tone it needs — checked against WCAG contrast rather than
 * guessed — so any brand produces readable buttons, links and text in both light and dark mode.
 * The seed is kept exact wherever it already passes; only a colour that would fail is moved, and
 * only in lightness.
 */
export function deriveBrandTheme(base: TenantThemeConfig, brand: BrandColors): TenantThemeConfig {
  const primary = seedOf(brand.primary);
  const secondary = seedOf(brand.secondary);
  const branded =
    base.mode === THEME_MODE.DARK
      ? deriveDark(base, primary, secondary)
      : deriveLight(base, primary, secondary);
  return { ...branded, id: `${base.id}-brand` };
}

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX_COLOR_PATTERN.test(value);
}

/** Both colours present and well-formed, or `null` — a half-set brand falls back to Olive's. */
export function toBrandColors(
  primary: string | null | undefined,
  secondary: string | null | undefined,
): BrandColors | null {
  if (typeof primary !== "string" || typeof secondary !== "string") {
    return null;
  }
  if (!isHexColor(primary) || !isHexColor(secondary)) {
    return null;
  }
  return { primary, secondary };
}
